import React from 'react';
import { BattleUnitState, TurnOrderEntry, HotbarSkill, BattleItem } from '../types/battle';
import { isUnitRooted, isUnitStunned, getDisabledReason } from '../utils/battleUtils';
import { playSfx } from '../utils/sound';

const ITEM_USE_AP_COST = 2;

interface BattleSidebarActionsProps {
  movementMode: boolean;
  attackMode: boolean;
  skillMode: boolean;
  itemMode: boolean;
  selectedUnit: BattleUnitState | null;
  currentTurnUnit: TurnOrderEntry | null;
  battleStatus: string;
  selectedSkill: HotbarSkill | null;
  selectedItem: BattleItem | null;
  battleItems: BattleItem[];
  actionError: string | null;
  onCancelMovement: () => void;
  onCancelAttack: () => void;
  onCancelSkill: () => void;
  onCancelItem: () => void;
  onEnterMovementMode: () => void;
  onEnterAttackMode: () => void;
  onSelectBattleItem: (item: BattleItem) => void;
  onEndTurn: () => void;
  onClearActionError: () => void;
  onInstantWin?: () => void;
}

const BattleSidebarActions: React.FC<BattleSidebarActionsProps> = ({
  movementMode,
  attackMode,
  skillMode,
  itemMode,
  selectedUnit,
  currentTurnUnit,
  battleStatus,
  selectedSkill,
  selectedItem,
  battleItems,
  actionError,
  onCancelMovement,
  onCancelAttack,
  onCancelSkill,
  onCancelItem,
  onEnterMovementMode,
  onEnterAttackMode,
  onSelectBattleItem,
  onEndTurn,
  onClearActionError,
  onInstantWin,
}) => {
  const isBattleOver = battleStatus === 'Victory' || battleStatus === 'Defeat';
  const isCurrentTurn = !!currentTurnUnit && !!selectedUnit && currentTurnUnit.unitId === selectedUnit.id;
  const canUseItems =
    !!selectedUnit &&
    selectedUnit.isPlayerUnit &&
    selectedUnit.stats.currentAP >= ITEM_USE_AP_COST &&
    !isBattleOver &&
    isCurrentTurn &&
    !isUnitStunned(selectedUnit);

  return (
    <div className="battle-actions">
      <h3>Actions</h3>
      {movementMode ? (
        <>
          <button className="action-btn cancel-btn" onClick={onCancelMovement}>
            Cancel Movement
          </button>
          <p className="movement-hint">Click on a highlighted tile to move there</p>
        </>
      ) : attackMode ? (
        <>
          <button className="action-btn cancel-btn" onClick={onCancelAttack}>
            Cancel Attack
          </button>
          <p className="attack-hint">Click on a red-highlighted enemy to attack</p>
        </>
      ) : skillMode ? (
        <>
          <button className="action-btn cancel-btn" onClick={onCancelSkill}>
            Cancel Skill
          </button>
          <p className="skill-hint">
            Using <strong>{selectedSkill?.skill.name}</strong> - Click on a target
          </p>
        </>
      ) : itemMode ? (
        <>
          <button className="action-btn cancel-btn" onClick={onCancelItem}>
            Cancel Item
          </button>
          <p className="skill-hint">
            Using <strong>{selectedItem?.itemName}</strong> - Click yourself or an adjacent ally
          </p>
        </>
      ) : (
        <>
          <button
            className="action-btn move-btn"
            onClick={() => { playSfx('uiClick'); onEnterMovementMode(); }}
            disabled={
              !selectedUnit ||
              !selectedUnit.isPlayerUnit ||
              selectedUnit.stats.currentMovement <= 0 ||
              isBattleOver ||
              !isCurrentTurn ||
              isUnitRooted(selectedUnit) ||
              isUnitStunned(selectedUnit)
            }
            title={getDisabledReason(selectedUnit, 'move') || undefined}
          >
            Move Unit ({selectedUnit?.stats.currentMovement || 0} pts)
          </button>
          <button
            className="action-btn attack-btn"
            onClick={() => { playSfx('uiClick'); onEnterAttackMode(); }}
            disabled={
              !selectedUnit ||
              !selectedUnit.isPlayerUnit ||
              selectedUnit.stats.currentAP <= 0 ||
              isBattleOver ||
              !isCurrentTurn ||
              isUnitStunned(selectedUnit)
            }
            title={getDisabledReason(selectedUnit, 'attack') || undefined}
          >
            Attack ({selectedUnit?.stats.currentAP || 0} AP)
          </button>
          {battleItems.length > 0 && (
            <div className="battle-items-panel">
              <div className="battle-items-header">Items ({ITEM_USE_AP_COST} AP)</div>
              {battleItems.map(item => (
                <button
                  key={item.itemName}
                  className="action-btn item-btn"
                  onClick={() => onSelectBattleItem(item)}
                  disabled={!canUseItems}
                  title={
                    !isCurrentTurn ? 'Not your turn' :
                    isUnitStunned(selectedUnit) ? 'Cannot use items while stunned' :
                    selectedUnit && selectedUnit.stats.currentAP < ITEM_USE_AP_COST ? `Requires ${ITEM_USE_AP_COST} AP` :
                    item.isAntidote ? 'Cures poison, burn, stun, and root' : `Heals ${item.healing} HP`
                  }
                >
                  {item.isAntidote ? '🧪' : '🧉'} {item.itemName} x{item.quantity}
                </button>
              ))}
            </div>
          )}
          <button
            className="action-btn end-turn-btn"
            onClick={() => { playSfx('uiClick'); onEndTurn(); }}
            disabled={!currentTurnUnit || !currentTurnUnit.isPlayerUnit || isBattleOver}
          >
            End Turn
          </button>
          {onInstantWin && (
            <button
              className="action-btn"
              onClick={onInstantWin}
              disabled={isBattleOver}
              style={{ backgroundColor: '#b36200', color: '#fff', width: '100%' }}
              title="DEBUG: Instantly win the battle (for testing)"
            >
              ⚡ Instant Win (DEBUG)
            </button>
          )}
        </>
      )}

      {actionError && (
        <div className="action-error">
          {actionError}
          <button onClick={onClearActionError} className="close-error">×</button>
        </div>
      )}
    </div>
  );
};

export default BattleSidebarActions;
